import type { CSSProperties, ReactNode } from "react";
import { useCallback, useRef, useState } from "react";
import { InfiniteScrollIndicator } from "@/components/InfiniteScrollIndicator";
import { Skeleton, SkeletonGroup } from "@/components/Skeleton";
import { type LoadingState, useInfiniteScroll } from "@/hooks/useInfiniteScroll";

export interface ListProps<T = unknown> {
  id?: string;
  items: T[];
  /** Render a row; receives item + selection/hover state */
  renderItem: (args: {
    item: T;
    index: number;
    isSelected: boolean;
    isHovered: boolean;
    // convenience for a11y bindings and event wiring
    rowProps: {
      id: string;
      role: string;
      "aria-selected": boolean;
      "aria-current"?: "page";
      tabIndex: number;
      onClick: (id: string) => void;
      onMouseEnter: (id: string) => void;
      onMouseLeave: (id: string) => void;
      onFocus: (id: string) => void;
      onBlur: (id: string) => void;
      ref?: (el: HTMLElement | null) => void;
    };
  }) => ReactNode;

  /** Controlled selection (single) */
  selectedId?: string | null;
  onSelect?: (id: string | null, item?: T) => void;
  /** Map/hover sync (e.g., highlight pin) */
  onHoverChange?: (id: string | null, item?: T) => void;

  /** Loading & error UI gates */
  isLoading?: boolean;
  /** Explicit loading state override (takes precedence over auto-detection) */
  loadingState?: LoadingState;
  error?: string | null;
  /** Custom skeleton for initial loading state - matches your data structure */
  renderLoadingSkeleton?: () => ReactNode;
  /** Number of skeleton rows to show during initial loading */
  loadingSkeletonCount?: number;

  /** Empty state */
  emptyMessage?: string;

  /** Density & dividers */
  variant?: "dense" | "relaxed";
  showDividers?: boolean;

  /** Infinite scroll */
  onLoadMore?: () => void | Promise<void>;
  hasMore?: boolean;
  /** Enable virtualization (default: true for 100+ items) */
  enableVirtualization?: boolean;
  /** Estimated size of each item in pixels */
  estimatedItemSize?: number;
  /** Custom loading indicator for loading-more state */
  loadingIndicator?: ReactNode;

  /** ARIA */
  "aria-label"?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * List
 *
 * A semantic wrapper and state manager for rows. Handles selection, hover,
 * keyboard navigation, accessibility, and infinite scroll with virtualization.
 * Composes with ListItem for visual rows.
 *
 * Example usage:
 * ```tsx
 * <List
 *   items={sites}
 *   selectedId={selectedSiteId}
 *   onSelect={(id) => openDetail(id)}
 *   onHoverChange={(id) => highlightOnMap(id)}
 *   renderItem={({ item, rowProps, isSelected }) => (
 *     <ListItem
 *       id={item.id}
 *       title={item.name}
 *       subtitle={item.address?.city}
 *       isSelected={isSelected}
 *       {...rowProps}
 *     />
 *   )}
 *   // Optional: Custom loading skeleton matching your data structure
 *   renderLoadingSkeleton={() => (
 *     <div className="flex items-center gap-3 px-4 py-3">
 *       <Skeleton width={40} height={40} variant="circle" />
 *       <div className="flex-1">
 *         <Skeleton width="60%" height={16} />
 *         <Skeleton width="40%" height={12} />
 *       </div>
 *     </div>
 *   )}
 *   loadingSkeletonCount={8}
 *   // Infinite scroll
 *   onLoadMore={loadMoreSites}
 *   hasMore={hasMoreSites}
 *   isLoading={isLoading}
 * />
 * ```
 */
export function List<T>({
  // id,
  items,
  renderItem,
  selectedId,
  onSelect,
  onHoverChange,
  isLoading = false,
  loadingState,
  error = null,
  renderLoadingSkeleton,
  loadingSkeletonCount = 5,
  emptyMessage = "No results found.",
  variant = "relaxed",
  showDividers = true,
  onLoadMore,
  hasMore = false,
  enableVirtualization,
  estimatedItemSize = 60,
  loadingIndicator,
  "aria-label": ariaLabel,
  className = "",
  style,
}: ListProps<T>) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Always call the hook to satisfy Rules of Hooks; it handles missing onLoadMore gracefully
  const {
    scrollRef: hookScrollRef,
    loadMoreRef,
    virtualizer,
    virtualItems,
    isLoadingMore,
    computedLoadingState,
  } = useInfiniteScroll({
    items,
    onLoadMore,
    hasMore,
    isLoading,
    loadingState,
    enableVirtualization,
    estimatedItemSize,
    overscan: 5,
  });

  // Use hook's scroll ref if available, otherwise fallback to local ref
  const scrollRef = hookScrollRef ?? listRef;

  // Handle item selection
  const handleSelect = useCallback(
    (itemId: string, item?: T) => {
      onSelect?.(itemId, item);
    },
    [onSelect]
  );

  // Handle hover changes for map sync
  const handleHoverChange = useCallback(
    (itemId: string | null, item?: T) => {
      setHoveredId(itemId);
      onHoverChange?.(itemId, item);
    },
    [onHoverChange]
  );

  // Helper to get stable item ID
  const getItemId = useCallback((item: T, index: number): string => {
    const candidate = (item as Record<string, unknown>)?.id;
    if (typeof candidate === "string" && candidate) {
      return candidate;
    }
    return `item-${index}`;
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!scrollRef.current || items.length === 0) return;

      const activeElement = document.activeElement;
      const currentIndex = items.findIndex((item, index) => {
        const itemId = getItemId(item, index);
        const element = itemRefs.current.get(itemId);
        return element && (element === activeElement || element.contains(activeElement));
      });

      let newIndex = currentIndex;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          newIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, items.length - 1);
          break;
        case "ArrowUp":
          event.preventDefault();
          newIndex = currentIndex === -1 ? 0 : Math.max(currentIndex - 1, 0);
          break;
        case "Home":
          event.preventDefault();
          newIndex = 0;
          break;
        case "End":
          event.preventDefault();
          newIndex = items.length - 1;
          break;
        default:
          return;
      }

      const targetId = getItemId(items[newIndex], newIndex);
      const targetElement = itemRefs.current.get(targetId);
      targetElement?.focus();
    },
    [items, getItemId, scrollRef]
  );

  // Base styles
  const baseStyles = [
    "flex flex-col",
    "bg-background-surface",
    "focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-2",
  ];

  // Density styles
  const densityStyles = {
    dense: "gap-0",
    relaxed: "gap-0",
  };

  // Divider styles
  const dividerStyles = showDividers ? "divide-y divide-border-muted" : "";

  const composedClassName = [...baseStyles, densityStyles[variant], dividerStyles, className].filter(Boolean).join(" ");

  // Initial loading state - only show full skeleton when no items and loading
  const isInitialLoad = computedLoadingState === "loading" || (isLoading && items.length === 0);

  if (isInitialLoad) {
    // Use custom skeleton if provided
    const skeletonContent = renderLoadingSkeleton ? (
      <>
        {Array.from({ length: loadingSkeletonCount }).map((_, index) => (
          <div key={`skeleton-${index}`}>{renderLoadingSkeleton()}</div>
        ))}
      </>
    ) : (
      // Default skeleton (avatar + title + subtitle + trailing)
      <SkeletonGroup count={loadingSkeletonCount} className="space-y-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <Skeleton width={16} height={16} variant="circle" />
          <div className="flex-1 space-y-2">
            <Skeleton width="75%" height={16} />
            <Skeleton width="50%" height={14} />
          </div>
          <Skeleton width={60} height={20} />
        </div>
      </SkeletonGroup>
    );

    return (
      <div
        ref={scrollRef}
        className={composedClassName}
        style={style}
        role="listbox"
        aria-label={ariaLabel}
        aria-busy="true"
        aria-live="polite"
      >
        {skeletonContent}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        ref={scrollRef}
        className={composedClassName}
        style={style}
        role="listbox"
        aria-label={ariaLabel}
        aria-live="polite"
      >
        <div className="p-4 text-center text-action-destructive">{error}</div>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div
        ref={scrollRef}
        className={composedClassName}
        style={style}
        role="listbox"
        aria-label={ariaLabel}
        aria-live="polite"
      >
        <div className="p-4 text-center text-text-muted">{emptyMessage}</div>
      </div>
    );
  }

  // Determine which items to render (virtualized or all)
  const itemsToRender = virtualizer
    ? virtualItems?.map((virtualItem) => ({
        virtualItem,
        item: items[virtualItem.index],
        index: virtualItem.index,
      })) || []
    : items.map((item, index) => ({ item, index, virtualItem: null }));

  // Render items
  return (
    <div
      ref={scrollRef}
      className={composedClassName}
      style={{
        ...style,
        // Infinite scroll requires overflow scroll and explicit height
        ...(onLoadMore && {
          overflow: "auto",
          height: style?.height || "600px", // Default height for infinite scroll
        }),
      }}
      role="listbox"
      aria-label={ariaLabel}
      aria-live="polite"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Virtualization container */}
      {virtualizer && (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {itemsToRender.map(({ virtualItem, item, index }) => {
            if (!virtualItem) return null;

            const itemId = getItemId(item, index);
            const isSelected = selectedId === itemId;
            const isHovered = hoveredId === itemId;

            const rowProps = {
              id: itemId,
              role: "option",
              "aria-selected": isSelected,
              "aria-current": isSelected ? ("page" as const) : undefined,
              tabIndex: isSelected ? 0 : selectedId == null && index === 0 ? 0 : -1,
              onClick: (id: string) => handleSelect(id, item),
              onMouseEnter: (id: string) => handleHoverChange(id, item),
              onMouseLeave: (_id: string) => handleHoverChange(null),
              onFocus: (id: string) => handleHoverChange(id, item),
              onBlur: (_id: string) => handleHoverChange(null),
              ref: (el: HTMLElement | null) => {
                if (el) {
                  itemRefs.current.set(itemId, el);
                } else {
                  itemRefs.current.delete(itemId);
                }
              },
            };

            return (
              <div
                key={itemId}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderItem({
                  item,
                  index,
                  isSelected,
                  isHovered,
                  rowProps,
                })}
              </div>
            );
          })}

          {/* Intersection observer target - positioned at the end of all virtual content */}
          {onLoadMore && hasMore && (
            <div
              ref={loadMoreRef}
              style={{
                position: "absolute",
                top: `${virtualizer.getTotalSize()}px`,
                left: 0,
                width: "100%",
                height: 1,
                pointerEvents: "none",
              }}
            />
          )}

          {/* Loading indicator - positioned at the end of all virtual content */}
          {isLoadingMore && (
            <div
              style={{
                position: "absolute",
                top: `${virtualizer.getTotalSize()}px`,
                left: 0,
                width: "100%",
              }}
            >
              {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
            </div>
          )}
        </div>
      )}

      {/* Non-virtualized rendering */}
      {!virtualizer && (
        <>
          {itemsToRender.map(({ item, index }) => {
            const itemId = getItemId(item, index);
            const isSelected = selectedId === itemId;
            const isHovered = hoveredId === itemId;

            const rowProps = {
              id: itemId,
              role: "option",
              "aria-selected": isSelected,
              "aria-current": isSelected ? ("page" as const) : undefined,
              tabIndex: isSelected ? 0 : selectedId == null && index === 0 ? 0 : -1,
              onClick: (id: string) => handleSelect(id, item),
              onMouseEnter: (id: string) => handleHoverChange(id, item),
              onMouseLeave: (_id: string) => handleHoverChange(null),
              onFocus: (id: string) => handleHoverChange(id, item),
              onBlur: (_id: string) => handleHoverChange(null),
              ref: (el: HTMLElement | null) => {
                if (el) {
                  itemRefs.current.set(itemId, el);
                } else {
                  itemRefs.current.delete(itemId);
                }
              },
            };

            return (
              <div key={itemId}>
                {renderItem({
                  item,
                  index,
                  isSelected,
                  isHovered,
                  rowProps,
                })}
              </div>
            );
          })}

          {/* Intersection observer target for non-virtualized infinite scroll */}
          {onLoadMore && hasMore && <div ref={loadMoreRef} style={{ height: 1 }} />}

          {/* Loading-more indicator for non-virtualized */}
          {isLoadingMore && (loadingIndicator || <InfiniteScrollIndicator state="loading-more" />)}
        </>
      )}
    </div>
  );
}
